import ArrowBackIosNewRounded from "@mui/icons-material/ArrowBackIosNewRounded";
import { Box, Button, Grid, Stack, useTheme } from "@mui/material";
import { useState } from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import { Proposal } from "../../types/proposal.types";
import { generateProposalPDF } from "../../utils/pdf-generator";
import apiService from "../../../../../service/Api";
import ActivityLogTable from "./components/view-proposal-page/ActivityLogTable";
import PdfHeader from "./components/view-proposal-page/PdfHeader";
import ProposalPagesPreview from "./components/view-proposal-page/ProposalPagesPreview";
import ProposalStatusSidebar from "./components/view-proposal-page/ProposalStatusSideBar";
import { CoverPageTemplate } from "../../types/cover.types";
import { tokens } from "../../../../../theme";

type ProposalStatus = "Accepted" | "Sent" | "Declined" | "Final" | "Draft";

const PROPOSAL_PAGES = [
  { id: "cover", label: "Cover Page" },
  { id: "main", label: "Letter & Overview" },
  { id: "memorial", label: "Memorial Page" },
  { id: "frequently", label: "Frequently Used Services" },
  { id: "services", label: "Included Services" },
  { id: "service", label: "Services Page" },
  { id: "whatsapp", label: "WhatsApp Preview" },
  { id: "testimonials", label: "Testimonials" },
  { id: "agreement", label: "Agreement Form" },
  { id: "backpage", label: "Back Page" },
];

interface ViewProposalPageProps {
  proposal: Proposal;
  status?: ProposalStatus;
  clientEmail?: string;
  onBack?: () => void;
  onUpdateProposal?: () => void;
  onStatusChange?: (id: number, status: string) => void;
  onGenerateInvoice?: () => void;
  onConfirmBooking?: () => void;
  previousStreams?: { id?: string; title: string; url: string }[];
  selectedCoverTemplate?: CoverPageTemplate | null;
}

export default function ViewProposalPage({
  proposal,
  status,
  clientEmail,
  onBack,
  onUpdateProposal,
  onStatusChange,
  onGenerateInvoice,
  onConfirmBooking,
  previousStreams,
  selectedCoverTemplate,
}: ViewProposalPageProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [currentStatus, setCurrentStatus] = useState<ProposalStatus>(
    status ?? "Draft",
  );
  const [pageSelectionOpen, setPageSelectionOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [mode, setMode] = useState<"download" | "print" | "share">("download");

  const createdBy =
    `${proposal.created_by?.first_name ?? ""} ${proposal.created_by?.last_name ?? ""}`.trim() ||
    "N/A";
  const updatedBy =
    `${proposal.last_updated_by?.first_name ?? ""} ${proposal.last_updated_by?.last_name ?? ""}`.trim() ||
    "N/A";

  const lastUpdated =
    proposal.updated_at && proposal.updated_at !== proposal.created_at
      ? {
          date: new Date(proposal.updated_at).toLocaleDateString(),
          by: updatedBy,
        }
      : undefined;

  const activityLog = [
    {
      date: proposal.created_at
        ? new Date(proposal.created_at).toLocaleDateString()
        : "",
      status: currentStatus,
      by: createdBy,
    },
  ];

  const handleDownload = () => {
    setMode("download");
    setPageSelectionOpen(true);
  };

  const handlePrint = () => {
    setMode("print");
    setPageSelectionOpen(true);
  };

  const handleShare = () => {
    if (!clientEmail) {
      showErrorToast("Client has no email address.");
      return;
    }
    setMode("share");
    setPageSelectionOpen(true);
  };

  const handleConfirmDownload = async (selectedPages: string[]) => {
    const pagesInOrder = PROPOSAL_PAGES.filter((p) =>
      selectedPages.includes(p.id),
    ).map((p) => p.id);

    try {
      setIsGenerating(true);

      if (mode === "print") {
        await generateProposalPDF(proposal, false, pagesInOrder, true);
        showSuccessToast("Sent to printer!");
        setPageSelectionOpen(false);
      } else if (mode === "download") {
        await generateProposalPDF(proposal, false, pagesInOrder);
        showSuccessToast("PDF downloaded successfully!");
        setPageSelectionOpen(false);
      } else if (mode === "share") {
        setIsSendingEmail(true);

        const blob = (await generateProposalPDF(
          proposal,
          true,
          pagesInOrder,
        )) as Blob;

        const formData = new FormData();
        formData.append(
          "pdf",
          blob,
          `proposal-${proposal.booking?.deceased?.[0]?.first_name || "document"}.pdf`,
        );
        const response = await apiService.post(
          `/api/bookings/booking-proposal/${proposal.id}/send-email/`,
          formData,
        );
        if (response.status === 200 || response.status === 201) {
          showSuccessToast(`Email sent successfully to ${clientEmail}!`);
          setCurrentStatus("Sent");
          onStatusChange?.(proposal.id, "Sent");
          setPageSelectionOpen(false);
        } else {
          showErrorToast("Failed to send email.");
        }
      }
    } catch (err: unknown) {
      if (mode === "share") {
        showErrorToast(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || "Error sending email.",
        );
      } else {
        showErrorToast(
          mode === "print" ? "Error printing" : "Error generating PDF",
        );
      }
    } finally {
      setIsGenerating(false);
      setIsSendingEmail(false);
    }
  };

  return (
    <Box>
      {/* Top bar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2.5 }}
      >
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIosNewRounded sx={{ fontSize: 13 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            color: colors.primary[100],
            p: 0,
            "&:hover": { bgcolor: "transparent", color: colors.primary[400] },
          }}
        >
          Back to all Proposals
        </Button>

        <Button
          onClick={onUpdateProposal}
          sx={{
            bgcolor: colors.primary[400],
            color: "white",
            borderRadius: "10px",
            px: 3,
            height: 38,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "13px",
            "&:hover": { bgcolor: colors.primary[500] },
          }}
        >
          Update Proposal
        </Button>
      </Stack>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 9 }}>
          <PdfHeader
            onPrint={handlePrint}
            onShare={handleShare}
            onDownload={handleDownload}
            bookingType={proposal.booking?.booking_type}
            isSending={isSendingEmail}
          />
          <ProposalPagesPreview
            selectedCoverTemplate={selectedCoverTemplate}
            proposal={proposal}
            previousStreams={previousStreams}
            pageSelectionOpen={pageSelectionOpen}
            isGenerating={isGenerating}
            onClosePageSelection={() => setPageSelectionOpen(false)}
            onConfirmDownload={handleConfirmDownload}
            mode={mode}
          />
          <ActivityLogTable logs={activityLog} lastUpdated={lastUpdated} />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <ProposalStatusSidebar
            status={currentStatus}
            proposalId={proposal.id}
            onStatusChange={(newStatus) => {
              setCurrentStatus(newStatus);
              onStatusChange?.(proposal.id, newStatus);
            }}
            onGenerateInvoice={onGenerateInvoice}
            onConfirmBooking={onConfirmBooking}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
